import { useMemo } from 'react';
import { PricingPlan } from '@cyberlabrs/helpers';
import { useAppSelector } from '../../store';
import { selectFeatureConfig } from '../../store/slices/feature-config.slice';
import { selectActiveSubscription } from '../../store/slices/active-subscription.slice';
import { useHttpGetFeatureConfigsOnMount } from '../organization/organizationApi';
import { ActiveSubscriptionResponseDto, FeatureConfigResponseDto, FeaturePricingPlanName } from '../../commonTypes/organization.types';
import { useFeatureFlag } from '../featureFlags/useFeatureFlags';
import { AppConfig } from '../../config/AppConfig';

type FeatureAvailabilityReason = 'PLAN' | 'CONFIG' | 'DISABLED' | null;

export type FeatureAvailability = {
    isAvailable: boolean;
    isDisabled: boolean;
    reason: FeatureAvailabilityReason;
    currentPlan?: FeaturePricingPlanName;
    requiredPlan?: FeaturePricingPlanName;
    featureConfig?: FeatureConfigResponseDto;
};

const PLAN_ORDER: FeaturePricingPlanName[] = [PricingPlan.FREE, PricingPlan.PREMIUM, PricingPlan.ENTERPRISE, PricingPlan.LIFETIME];

export const useFeatureAvailability = (featureName: string): FeatureAvailability => {
    // Ensure configs are loaded
    useHttpGetFeatureConfigsOnMount();

    const configs = useAppSelector(selectFeatureConfig);
    const activeSubscription = useAppSelector(selectActiveSubscription);
    const billingEnabled = useFeatureFlag('BILLING');
    const isDevelopment = AppConfig.isDevelopment;

    return useMemo(() => {
        // Derive current plan from active subscription response (needed for tooltip display)
        const subscription = activeSubscription as ActiveSubscriptionResponseDto | null;
        const rawPlanName = subscription?.name?.toUpperCase() as FeaturePricingPlanName | undefined;
        const currentPlan: FeaturePricingPlanName = rawPlanName && PLAN_ORDER.includes(rawPlanName) ? rawPlanName : PricingPlan.FREE;

        if (!billingEnabled && !isDevelopment) {
            return {
                isAvailable: true,
                isDisabled: false,
                reason: null,
                currentPlan,
            };
        }

        const config = configs.find(c => c.name === featureName);

        if (isDevelopment && billingEnabled && config) {
            const planConfig = config.config.subscription[currentPlan];
            const isEnabledForPlan = planConfig?.enabled === true;

            if (isEnabledForPlan) {
                return {
                    isAvailable: true,
                    isDisabled: false,
                    reason: null,
                    currentPlan,
                    featureConfig: config,
                };
            }
        }

        if (!config) {
            return {
                isAvailable: false,
                isDisabled: true,
                reason: 'DISABLED',
            };
        }

        if (!config.enabled) {
            return {
                isAvailable: false,
                isDisabled: true,
                reason: 'CONFIG',
                featureConfig: config,
            };
        }

        if (currentPlan === PricingPlan.LIFETIME) {
            return {
                isAvailable: true,
                isDisabled: false,
                reason: null,
                currentPlan,
                featureConfig: config,
            };
        }

        const planConfig = config.config.subscription[currentPlan];

        if (!planConfig?.enabled) {
            const required = PLAN_ORDER.find(p => config.config.subscription[p]?.enabled === true);

            return {
                isAvailable: false,
                isDisabled: true,
                reason: 'PLAN',
                currentPlan,
                requiredPlan: required,
                featureConfig: config,
            };
        }

        return {
            isAvailable: true,
            isDisabled: false,
            reason: null,
            currentPlan,
            featureConfig: config,
        };
    }, [configs, featureName, activeSubscription, billingEnabled, isDevelopment]);
};
